export interface AssetSetMembershipIdentity {
    ID: string;
    VersionToken?: string | null;
    ScopeToken?: string | null;
}

type AssetSetMembershipLoader = (
    assetSetID: string
) => Promise<Array<string | null | undefined> | null | undefined>;

export interface AssetSetMembershipBatchResult {
    ID: string;
    VersionToken?: string | null;
    AssetObjectIDs?: Array<string | null | undefined> | null;
}

type AssetSetMembershipBatchLoader = (
    assetSets: AssetSetMembershipIdentity[]
) => Promise<AssetSetMembershipBatchResult[]>;

export type AssetSetMembershipResolution =
    | { status: 'fulfilled'; value: string[] }
    | { status: 'rejected'; reason: unknown };

const getMembershipCacheKey = (
    assetSet: AssetSetMembershipIdentity
): string => [
    assetSet.ID.trim().toLowerCase(),
    assetSet.VersionToken ?? '',
    getMembershipScopeToken(assetSet),
].join('|');

const getMembershipScopeToken = (
    assetSet: AssetSetMembershipIdentity
): string => assetSet.ScopeToken?.trim().toLowerCase() ?? '';

const MAX_RESOLVED_ONTOLOGY_SCOPES = 5;

export default class AssetSetMembershipCache {
    private readonly resolvedMemberships = new Map<string, string[]>();
    private readonly pendingMemberships = new Map<string, Promise<string[]>>();
    private readonly resolvedCacheKeysByScope = new Map<string, Set<string>>();
    private readonly recentScopeTokens: string[] = [];
    private generation = 0;

    clear(): void {
        this.generation++;
        this.resolvedMemberships.clear();
        this.pendingMemberships.clear();
        this.resolvedCacheKeysByScope.clear();
        this.recentScopeTokens.length = 0;
    }

    invalidate(assetSetID: string): void {
        const keyPrefix = `${assetSetID.trim().toLowerCase()}|`;
        this.generation++;

        for (const key of this.resolvedMemberships.keys()) {
            if (key.startsWith(keyPrefix)) this.deleteResolvedMembership(key);
        }
        for (const key of this.pendingMemberships.keys()) {
            if (key.startsWith(keyPrefix)) this.pendingMemberships.delete(key);
        }
    }

    async resolve(
        assetSet: AssetSetMembershipIdentity,
        loader: AssetSetMembershipLoader
    ): Promise<string[]> {
        const cacheKey = getMembershipCacheKey(assetSet);
        const scopeToken = getMembershipScopeToken(assetSet);
        this.touchScope(scopeToken);
        const resolved = this.resolvedMemberships.get(cacheKey);
        if (resolved) {
            return resolved.slice();
        }

        const pending = this.pendingMemberships.get(cacheKey);
        if (pending) {
            return (await pending).slice();
        }

        const requestGeneration = this.generation;
        const request = loader(assetSet.ID)
            .then(children => {
                const assetIDs = Array.from(new Set(
                    (children ?? []).filter((child): child is string => !!child)
                ));
                if (this.generation === requestGeneration && this.isScopeRetained(scopeToken)) {
                    this.setResolvedMembership(cacheKey, scopeToken, assetIDs);
                }
                return assetIDs;
            })
            .finally(() => {
                if (this.pendingMemberships.get(cacheKey) === request) {
                    this.pendingMemberships.delete(cacheKey);
                }
            });

        this.pendingMemberships.set(cacheKey, request);
        return (await request).slice();
    }

    async resolveMany(
        assetSets: AssetSetMembershipIdentity[],
        loader: AssetSetMembershipBatchLoader
    ): Promise<string[][]> {
        return Promise.all(this.createResolveManyRequests(assetSets, loader));
    }

    async resolveManySettled(
        assetSets: AssetSetMembershipIdentity[],
        loader: AssetSetMembershipBatchLoader
    ): Promise<AssetSetMembershipResolution[]> {
        return Promise.all(this.createResolveManyRequests(assetSets, loader).map(request =>
            request.then(
                value => ({ status: 'fulfilled' as const, value }),
                reason => ({ status: 'rejected' as const, reason })
            )));
    }

    private createResolveManyRequests(
        assetSets: AssetSetMembershipIdentity[],
        loader: AssetSetMembershipBatchLoader
    ): Promise<string[]>[] {
        const uniqueAssetSets = new Map<string, AssetSetMembershipIdentity>();
        for (const assetSet of assetSets) {
            const cacheKey = getMembershipCacheKey(assetSet);
            this.touchScope(getMembershipScopeToken(assetSet));
            if (!uniqueAssetSets.has(cacheKey)) uniqueAssetSets.set(cacheKey, assetSet);
        }

        const requestsByCacheKey = new Map<string, Promise<string[]>>();
        const missingAssetSets: AssetSetMembershipIdentity[] = [];

        for (const [cacheKey, assetSet] of uniqueAssetSets) {
            const resolved = this.resolvedMemberships.get(cacheKey);
            if (resolved) {
                requestsByCacheKey.set(cacheKey, Promise.resolve(resolved.slice()));
                continue;
            }

            const pending = this.pendingMemberships.get(cacheKey);
            if (pending) {
                requestsByCacheKey.set(cacheKey, pending);
                continue;
            }

            missingAssetSets.push(assetSet);
        }

        if (missingAssetSets.length > 0) {
            const requestGeneration = this.generation;
            const batchRequest = loader(missingAssetSets.map(assetSet => ({ ...assetSet })))
                .then(rows => {
                    const rowsByID = new Map(
                        (rows ?? []).map(row => [row.ID.trim().toLowerCase(), row])
                    );
                    const resolvedBatch = new Map<string, string[] | Error>();

                    for (const assetSet of missingAssetSets) {
                        const cacheKey = getMembershipCacheKey(assetSet);
                        const row = rowsByID.get(assetSet.ID.trim().toLowerCase());
                        if (!row) {
                            resolvedBatch.set(cacheKey, new Error(
                                `Asset Set membership batch omitted '${assetSet.ID}'.`
                            ));
                            continue;
                        }
                        if (row.VersionToken != null &&
                            row.VersionToken !== (assetSet.VersionToken ?? '')) {
                            resolvedBatch.set(cacheKey, new Error(
                                `Asset Set membership changed while loading '${assetSet.ID}'.`
                            ));
                            continue;
                        }

                        const assetIDs = Array.from(new Set(
                            (row.AssetObjectIDs ?? []).filter((child): child is string => !!child)
                        ));
                        resolvedBatch.set(cacheKey, assetIDs);
                        const scopeToken = getMembershipScopeToken(assetSet);
                        if (this.generation === requestGeneration && this.isScopeRetained(scopeToken)) {
                            this.setResolvedMembership(cacheKey, scopeToken, assetIDs);
                        }
                    }

                    return resolvedBatch;
                });

            for (const assetSet of missingAssetSets) {
                const cacheKey = getMembershipCacheKey(assetSet);
                const request = batchRequest
                    .then(resolvedBatch => {
                        const result = resolvedBatch.get(cacheKey)!;
                        if (result instanceof Error) throw result;
                        return result.slice();
                    })
                    .finally(() => {
                        if (this.pendingMemberships.get(cacheKey) === request) {
                            this.pendingMemberships.delete(cacheKey);
                        }
                    });
                this.pendingMemberships.set(cacheKey, request);
                requestsByCacheKey.set(cacheKey, request);
            }
        }

        return assetSets.map(assetSet =>
            requestsByCacheKey.get(getMembershipCacheKey(assetSet))!
                .then(assetIDs => assetIDs.slice()));
    }

    private touchScope(scopeToken: string): void {
        if (!scopeToken) return;

        const existingIndex = this.recentScopeTokens.indexOf(scopeToken);
        if (existingIndex >= 0) this.recentScopeTokens.splice(existingIndex, 1);
        this.recentScopeTokens.push(scopeToken);

        while (this.recentScopeTokens.length > MAX_RESOLVED_ONTOLOGY_SCOPES) {
            const evictedScope = this.recentScopeTokens.shift()!;
            const cacheKeys = this.resolvedCacheKeysByScope.get(evictedScope);
            if (cacheKeys) {
                for (const cacheKey of cacheKeys) this.resolvedMemberships.delete(cacheKey);
            }
            this.resolvedCacheKeysByScope.delete(evictedScope);
        }
    }

    private isScopeRetained(scopeToken: string): boolean {
        return !scopeToken || this.recentScopeTokens.includes(scopeToken);
    }

    private setResolvedMembership(cacheKey: string, scopeToken: string, assetIDs: string[]): void {
        this.resolvedMemberships.set(cacheKey, assetIDs);
        if (!scopeToken) return;

        let cacheKeys = this.resolvedCacheKeysByScope.get(scopeToken);
        if (!cacheKeys) {
            cacheKeys = new Set<string>();
            this.resolvedCacheKeysByScope.set(scopeToken, cacheKeys);
        }
        cacheKeys.add(cacheKey);
    }

    private deleteResolvedMembership(cacheKey: string): void {
        this.resolvedMemberships.delete(cacheKey);
        for (const [scopeToken, cacheKeys] of this.resolvedCacheKeysByScope) {
            cacheKeys.delete(cacheKey);
            if (cacheKeys.size === 0) this.resolvedCacheKeysByScope.delete(scopeToken);
        }
    }
}
